import { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { InputGroupContext } from './utils';
import type { TrackingProp } from '../types';
import { useTracking, getLabel } from '../utils';
import type { InputProps } from '../input/input';
import styles from './input-group.module.less';

type InputGroupProps = Omit<
  React.ComponentPropsWithoutRef<'fieldset'>,
  'onChange'
> & {
  defaultValue?: InputProps['value'];
  disabled?: boolean;
  invalid?: boolean;
  name: string;
  onChange?: (values: string[]) => void;
  required?: boolean;
  tracking?: TrackingProp;
  type?: 'radio' | 'checkbox';
  value?: InputProps['value'];
  variant?: 'row' | 'row-background' | 'column' | 'column-background';
};

export const InputGroup = forwardRef<HTMLFieldSetElement, InputGroupProps>(
  function InputGroup(
    {
      children,
      className,
      defaultValue,
      disabled,
      invalid,
      name,
      onChange,
      required,
      tracking = true,
      type = 'radio',
      value,
      variant = 'row',
      ...rest
    },
    ref
  ) {
    // Support both value (controlled) and defaultValue (uncontrolled)
    // Read more on https://react.dev/reference/react-dom/components/input#optimizing-re-rendering-on-every-keystroke
    const [defaults, setDefaults] = useState(defaultValue);
    const values = toStrings(typeof value === 'undefined' ? defaults : value);
    const classes = clsx(styles.inputGroup, styles[variant], className);
    const track = useTracking(tracking);

    const handleChange = ({
      target: input,
      currentTarget: group
    }: React.ChangeEvent<HTMLFieldSetElement>) => {
      if (!(input instanceof HTMLInputElement) || input.name !== name) return; // Only track inputs with same name the <InputGroup>
      const legend = group.querySelector('legend') as HTMLLegendElement;
      const checked = group.querySelectorAll<HTMLInputElement>('input:checked');
      const labels = Array.from(checked, getLabel);
      const values = Array.from(checked, ({ value }) => value);

      setDefaults(values);
      track({
        eventCategory: `inputgroup_${type}`,
        eventAction: input.checked ? 'checked' : 'unchecked',
        eventLabel: getLabel(legend),
        eventStatus: labels.sort().join('_')
      });
      onChange?.(values); // Track before onChange in case URL changes
    };

    const useGroupProps = (props: InputProps) => {
      if (props.name && props.name !== name) return; // Only take control if input name is missing or same as InputGroup name
      props['aria-invalid'] = props.invalid || invalid;
      props.checked = values.includes(String(props.value));
      props.name = name;
      props.onChange = props.onChange || (() => {});
      props.required = props.required || required;
      props.disabled = props.disabled || disabled;
      props.tracking = false; // Let input group do the tracking
      props.type = props.type || type;
    };

    return (
      <fieldset {...rest} className={classes} onChange={handleChange} ref={ref}>
        <InputGroupContext.Provider value={useGroupProps}>
          {children}
        </InputGroupContext.Provider>
      </fieldset>
    );
  }
);

const toStrings = (value: unknown) =>
  (Array.isArray(value) ? value : [value]).map(String);
